/**
 * Helpers para preservar a URL de destino quando o usuário precisa autenticar
 * antes de acessar uma rota protegida (ex.: deep-link vindo de email).
 *
 * Fluxo:
 *   1. Algum ponto do app detecta que precisa de auth (componente protegido,
 *      interceptor 401, etc.) → chama `preservePostLoginRedirect()` para salvar
 *      a URL atual em sessionStorage antes de redirecionar para /login.
 *   2. Após login bem-sucedido, `Login.tsx` chama `resolvePostLoginRedirect()`
 *      para descobrir para onde voltar (sessionStorage > location.state.from > fallback).
 */

const STORAGE_KEY = 'postLoginRedirect'
const DEFAULT_TARGET = '/manutencao'

/**
 * Salva a URL atual em sessionStorage para uso pós-login.
 * Não salva se a URL já é a raiz ou /login (não há para onde voltar).
 */
export const preservePostLoginRedirect = (): void => {
  try {
    const path = window.location.pathname + window.location.search
    if (path && path !== '/' && !path.startsWith('/login')) {
      sessionStorage.setItem(STORAGE_KEY, path)
    }
  } catch {
    // sessionStorage pode falhar em modo privado/SSR — fail-open
  }
}

/**
 * Resolve para onde redirecionar o usuário após um login bem-sucedido.
 * Prioridade: sessionStorage > location.state.from > /manutencao.
 * Consome (remove) a entrada do sessionStorage quando usada.
 */
export const resolvePostLoginRedirect = (locationState: unknown): string => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY)
    if (stored) {
      sessionStorage.removeItem(STORAGE_KEY)
      return stored
    }
  } catch {
    // ignore
  }

  const from = (locationState as { from?: { pathname?: string; search?: string } } | null)?.from
  if (from?.pathname && from.pathname !== '/' && !from.pathname.startsWith('/login')) {
    return from.pathname + (from.search || '')
  }
  return DEFAULT_TARGET
}
